/**
 * OmniClinicalBridge Responsive Hooks
 * OmniAlera Technologies Holdings Inc.
 * 
 * React hooks for responsive design across all device sizes
 */

import { useState, useEffect, useCallback } from 'react';

// Breakpoints matching Tailwind CSS defaults
export const BREAKPOINTS = {
  xs: 320,   // Extra small phones
  sm: 640,   // Small phones
  md: 768,   // Tablets
  lg: 1024,  // Laptops
  xl: 1280,  // Desktops
  '2xl': 1536 // Large screens
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

interface ScreenSize {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isPortrait: boolean;
  isLandscape: boolean;
}

/**
 * Hook to get current screen size and breakpoint
 */
export function useScreenSize(): ScreenSize {
  const getScreenSize = useCallback((): ScreenSize => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const height = typeof window !== 'undefined' ? window.innerHeight : 768;
    
    let breakpoint: Breakpoint = 'xs';
    if (width >= BREAKPOINTS['2xl']) breakpoint = '2xl';
    else if (width >= BREAKPOINTS.xl) breakpoint = 'xl';
    else if (width >= BREAKPOINTS.lg) breakpoint = 'lg';
    else if (width >= BREAKPOINTS.md) breakpoint = 'md';
    else if (width >= BREAKPOINTS.sm) breakpoint = 'sm';
    
    return {
      width,
      height,
      breakpoint,
      isMobile: width < BREAKPOINTS.md,
      isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
      isDesktop: width >= BREAKPOINTS.lg,
      isPortrait: height > width,
      isLandscape: width > height
    };
  }, []);
  
  const [screenSize, setScreenSize] = useState<ScreenSize>(getScreenSize);
  
  useEffect(() => {
    const handleResize = () => {
      setScreenSize(getScreenSize());
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [getScreenSize]);
  
  return screenSize;
}

/**
 * Hook to check if screen matches a breakpoint
 */
export function useBreakpoint(breakpoint: Breakpoint): boolean {
  const { width } = useScreenSize();
  return width >= BREAKPOINTS[breakpoint];
}

/**
 * Hook for media queries
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);
    
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);
  
  return matches;
}

/**
 * Hook for touch device detection
 */
export function useTouchDevice(): boolean {
  const [isTouch, setIsTouch] = useState(false);
  
  useEffect(() => {
    setIsTouch(
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0
    );
  }, []);
  
  return isTouch;
}

/**
 * Hook for scroll position
 */
export function useScrollPosition(): {
  scrollX: number;
  scrollY: number;
  scrollDirection: 'up' | 'down' | null;
} {
  const [scrollPosition, setScrollPosition] = useState({
    scrollX: 0,
    scrollY: 0,
    scrollDirection: null as 'up' | 'down' | null
  });
  
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const direction = currentScrollY > lastScrollY ? 'down' : 'up';
          
          setScrollPosition({
            scrollX: window.scrollX,
            scrollY: currentScrollY,
            scrollDirection: Math.abs(currentScrollY - lastScrollY) > 5 ? direction : null
          });
          
          lastScrollY = currentScrollY;
          ticking = false;
        });
        
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return scrollPosition;
}

/**
 * Hook for keyboard visibility (mobile)
 */
export function useKeyboardVisibility(): boolean {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const initialHeight = window.innerHeight;
    
    const handleResize = () => {
      // If height decreased significantly, keyboard is likely visible
      const currentHeight = window.innerHeight;
      setIsKeyboardVisible(initialHeight - currentHeight > 150);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isKeyboardVisible;
}

/**
 * Hook for safe area insets (notch, home indicator)
 */
export function useSafeAreaInsets(): {
  top: number;
  right: number;
  bottom: number;
  left: number;
} {
  const [insets, setInsets] = useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  });
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const computedStyle = getComputedStyle(document.documentElement);
    
    setInsets({
      top: parseInt(computedStyle.getPropertyValue('--sat') || '0', 10),
      right: parseInt(computedStyle.getPropertyValue('--sar') || '0', 10),
      bottom: parseInt(computedStyle.getPropertyValue('--sab') || '0', 10),
      left: parseInt(computedStyle.getPropertyValue('--sal') || '0', 10)
    });
  }, []);
  
  return insets;
}
